/**
 * OVERRIDE Body.jsx
 * REASON: Replace semantic ui embded with @fhnw/components/Embed to support more
 * video sources and to have a better control over the player.
 * FILE: https://github.com/plone/volto/blob/main/packages/volto/src/components/manage/Blocks/Video/Body.jsx
 * FILE VERSION: Volto 18.0.0-alpha.43
 * TICKET: https://gitlab.fhnw.ch/webteam/fhnw-webauftritt-2025/-/issues/1259
 * DATE: 2026-02-06
 * DEVELOPER: @skaeser
 */

// START CUSTOMIZATION
import React, { useState, useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { Embed } from '@fhnw/components/Embed';
import cx from 'classnames';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers';

const messages = defineMessages({
  video_placeholder: {
    id: 'block.video.placeholder',
    defaultMessage: 'Click to play video',
  },
});

//Extracting videoID, listID and thumbnailURL from the video URL
const getVideoIDAndPlaceholder = (url) => {
  let videoID = null;
  let listID = null;
  let thumbnailURL = null;

  if (url) {
    if (url.match('youtu')) {
      if (url.match('list')) {
        const matches = url.match(/^.*\?list=(.*)|^.*&list=(.*)$/);
        listID = matches[1] || matches[2];

        let thumbnailID = null;
        if (url.match(/\?v=(.*)&list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)&list(.*)/)[1];
        }
        if (url.match(/\?v=(.*)\?list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)\?list(.*)/)[1];
        }
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/maxresdefault.jpg';
      } else if (url.match('live')) {
        videoID = url.match(/^.*\/live\/(.*)/)[1];
      } else if (url.match(/\.be\//)) {
        videoID = url.match(/^.*\.be\/(.*)/)[1];
      } else if (url.match(/shorts/)) {
        videoID = url.match(/^.*\/shorts\/(.*)/)[1];
      } else if (url.match(/\?v=/)) {
        videoID = url.match(/^.*\?v=(.*)$/)[1];
      }

      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        //load video preview image from youtube
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/maxresdefault.jpg';
      }
    } else if (url.match('vimeo')) {
      videoID = url.match(/^.*\.com\/(.*)/)[1];
      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        thumbnailURL = 'https://vumbnail.com/' + thumbnailID + '.jpg';
      }
    }
  }
  return { videoID, listID, thumbnailURL };
};

const embedStyle = {
  width: '100%',
  height: '100%',
};

const Body = forwardRef(
  ({ data, autoplay, controls = true, forceCaptions = false }, _ref) => {
    const intl = useIntl();
    const [paused, setPaused] = useState(!autoplay && !controls);
    let placeholder = data.preview_image
      ? isInternalURL(data.preview_image)
        ? `${flattenToAppURL(data.preview_image)}/@@images/image`
        : data.preview_image
      : null;

    const isYoutubeShort =
      typeof data.url === 'string' && data.url.match(/shorts/);

    const { listID, thumbnailURL } = useMemo(
      () => getVideoIDAndPlaceholder(data.url),
      [data.url],
    );

    placeholder = !placeholder ? thumbnailURL : placeholder;

    const _newRef = React.createRef();
    const ref = _ref || _newRef;

    const playIcon = useMemo(() => {
      return (
        <Icon
          name="play"
          title={intl.formatMessage(messages.video_placeholder)}
        />
      );
    }, [intl]);
    const fallback = useMemo(() => {
      return <img className="placeholder" alt="" src={placeholder} />;
    }, [placeholder]);

    const embedSettings = {
      src: data.url.match('list')
        ? `https://www.youtube.com/embed/videoseries?list=${listID}`
        : data.url,
      light: !autoplay ? placeholder : false,
      playIcon: playIcon,
      autoPlay: !!autoplay || !!placeholder,
      muted: !!autoplay || !controls,
      controls: !!controls,
      loop: !!autoplay,
      pip: true,
      fallback: fallback,
      style: embedStyle,
      title: data?.title || data?.image_title || '',
      config: {
        youtube: {
          // 1 forces captions on, automatic ones included; 0 leaves it to the
          // viewer's own YouTube setting. Passing 0 is not the same as passing
          // nothing: <youtube-video> defaults to 1 on its own (#1710).
          cc_load_policy: forceCaptions ? 1 : 0,
          // Videos with real subtitles offer one track per language, and this
          // is the one the viewer came for. Embed picks the track by it.
          ...(forceCaptions ? { cc_lang_pref: intl.locale } : {}),
        },
      },
    };

    return (
      <>
        {data.url && (
          <div
            ref={ref}
            className={cx('video-inner', {
              'full-width': data.align === 'full',
              internal: isInternalURL(data.url),
              short: isYoutubeShort,
            })}
          >
            {isInternalURL(data.url) ? (
              <>
                {paused && playIcon}
                <video
                  src={
                    data.url.includes('@@download')
                      ? data.url
                      : `${flattenToAppURL(data.url)}/@@download/file`
                  }
                  controls={!!controls}
                  autoPlay={!!autoplay}
                  loop={!!autoplay}
                  muted={!!autoplay || !controls}
                  poster={!autoplay ? placeholder : undefined}
                  type="video/mp4"
                  title={embedSettings.title}
                  onClick={(e) => {
                    if (!!controls) return;
                    if (e.target.paused) {
                      e.target.play();
                      setPaused(false);
                    } else {
                      e.target.pause();
                      setPaused(true);
                    }
                  }}
                >
                  <track kind="captions" />
                </video>
              </>
            ) : (
              <Embed {...embedSettings} />
            )}
          </div>
        )}
      </>
    );
  },
);

// END CUSTOMIZATION

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Body.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  autoplay: PropTypes.bool,
  controls: PropTypes.bool,
  forceCaptions: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

export default Body;
export { getVideoIDAndPlaceholder };
